import moment from 'moment'
import Select from '@atlaskit/select'
import { useSnackbar } from 'notistack'
import React, { useState } from 'react'
import { Drawer } from 'antd'

import { notifOps, usePet } from '../../modules'

import Datepicker from '../reusable/datePicker'
import SelectLabel from '../reusable/selectLabel'
import Confirm from '../reusable/confirm'

const RecordInsurerPayment = ({ visible, submissions = [], insurer, onClose, onPerform }) => {
    const depositAccountTypes = ['Cash', 'Bank']
    const submissionsTotal = submissions.reduce((total, sub) => total + (sub.outstandingAmount ?? (sub.totalAmount - sub.paidAmount)), 0)

    const [date, setDate] = useState(moment())
    const [amount, setAmount] = useState(submissionsTotal)
    const [ref, setRef] = useState('')
    const [account, setAccount] = useState()
    const [method, setMethod] = useState()

    const [accounts, setAccounts] = useState([])
    const [methods, setMethods] = useState([])

    const { enqueueSnackbar } = useSnackbar()

    usePet('accounts', { string: '', limit: 100, types: depositAccountTypes }, { notify: true, callback: ({ data }) => {
        let obj = {}

        data?.accounts?.forEach(acc => {
            if (!obj[acc.type]) obj[acc.type] = []
            obj[acc.type].push({ label: acc.name, value: acc._id })
        })

        let final = []
        for (let typ in obj) {
            final.push({ label: typ, options: obj[typ] })
        }

        setAccounts(final)
    } })

    usePet('paymentMethodNames', { type: 'regular' }, { notify: true, callback: ({ data }) => setMethods(data) })

    const close = () => {
        onClose()
    }

    const onComplete = (_, notif, ops) => {
        onPerform()
        notif('Payment has been recorded', ops('success'))
        close()
    }

    const [{ loading }, execute] = usePet('recordInsurerPayment', {
        insurer: insurer?._id,
        submissions: submissions.map(sub => sub._id ?? sub.id),
        payments: [
            {
                method: method?.value,
                account: account?.value,
                amount,
                ref,
            }
        ],
        date,
    }, { notify: true, callback: onComplete, manual: true })

    const modify = e => {
        if (e.target.name === 'amount') {
            if (Number(e.target.value) > submissionsTotal) {
                enqueueSnackbar(`Payment amount cannot be higher than the outstanding total (Ksh. ${submissionsTotal})`, notifOps('warning'))
                return
            }

            setAmount(e.target.value)
        }

        if (e.target.name === 'ref') setRef(e.target.value)
    }

    const enableAmount = amount > 0 && amount <= submissionsTotal

    const recordPayment = () => {
        if (!method) enqueueSnackbar('Please indicate the payment method', notifOps('warning'))
        if (!account) enqueueSnackbar('Please indicate the payment account', notifOps('warning'))
        if (!enableAmount) enqueueSnackbar('Please enter the correct format for amount', notifOps('warning'))

        if (method && account && enableAmount)
            Confirm({
                title: `Record payment of Ksh. ${amount}?`,
                message: `This will reduce ${insurer?.name}'s outstanding balance by Ksh. ${amount}, deposited into ${account?.label}`,
                okText: 'Record Payment',
                onOk: execute
            })
    }

    return (
        <Drawer
            closable
            maskClosable
            visible={ visible }
            onClose={ close }
            footer={
                <div className="w-full text-right">
                    <button disabled={ loading } onClick={ recordPayment } className="text-white hisani-btn-orange font-medium rounded-lg text-sm px-5 py-2.5 text-center">Save</button>
                </div>
            }
            width="35%">
            <p className="font-extrabold text-3xl mb-0">Record Insurer Payment</p>
            <p className="text-sm italic mb-0 text-gray-700">{ insurer?.name ?? 'Insurer' } — Outstanding: <span className="font-semibold">Ksh. { submissionsTotal }</span></p>
            <div className="flex flex-wrap mt-3">
                <div className="w-full px-3 mb-1">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Date
                    </label>
                    <Datepicker value={ date } onChange={ setDate } />
                </div>
                <div className="w-full px-3 mb-1">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Deposited Into
                    </label>
                    <Select
                        className="mb-3"
                        value={ account }
                        onChange={ setAccount }
                        formatGroupLabel={ SelectLabel }
                        options={ accounts }
                        placeholder="Select account" />
                    <p className="text-gray-500 text-xs italic">Only cash and bank destination accounts are shown here so insurer settlements post into deposit-ready accounts.</p>
                </div>
                <div className="w-full px-3 mb-1">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Payment Method
                    </label>
                    <Select
                        className="mb-3"
                        value={ method }
                        onChange={ setMethod }
                        options={ methods.map(method => ({ label: method, value: method })) }
                        placeholder="Select payment method" />
                </div>
                <div className="w-full px-3 mb-1">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Amount Received
                    </label>
                    <input
                        className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                        type="number"
                        name="amount"
                        value={ amount }
                        onChange={ modify }
                        placeholder="2000" />
                </div>
                <div className="w-full px-3 mb-1">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Transaction Ref <span className="text-gray-500 text-xs normal-case italic">(Optional)</span>
                    </label>
                    <input
                        className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                        type="text"
                        name="ref"
                        value={ ref }
                        onChange={ modify }
                        placeholder="XXXXXX" />
                    <p className="text-gray-500 text-xs italic">Indicate any transaction codes, cheque numbers, etc</p>
                </div>
            </div>
        </Drawer>
    )
}

export default RecordInsurerPayment
